<template>
  <div class="dashboard-cliente">
    <b-overlay :show="isLoading" variant="light" blur="2px">
      <div class="page-header">
        <div class="header-top">
          <div>
            <h2 class="page-title">Minha Conta</h2>
            <p class="page-subtitle">Acompanhe seus pedidos, notas fiscais e financeiro</p>
          </div>
          <div class="period-controls">
            <form class="filters-inline" @submit.prevent="loadData">
              <div class="date-field">
                <label for="cliente-dashboard-inicio">Data inicial</label>
                <b-form-datepicker
                  id="cliente-dashboard-inicio"
                  v-model="filters.dataInicio"
                  locale="pt-BR"
                  :date-format-options="shortDateOptions"
                  placeholder="dd/mm/aaaa"
                  :disabled="isLoading"
                  size="sm"
                  class="filter-date"
                />
              </div>
              <div class="date-field">
                <label for="cliente-dashboard-fim">Data final</label>
                <b-form-datepicker
                  id="cliente-dashboard-fim"
                  v-model="filters.dataFim"
                  locale="pt-BR"
                  :date-format-options="shortDateOptions"
                  placeholder="dd/mm/aaaa"
                  :disabled="isLoading"
                  size="sm"
                  class="filter-date"
                />
              </div>
              <b-button variant="primary" size="sm" type="submit" :disabled="isLoading || !!filterError">
                <feather-icon icon="FilterIcon" size="14" /> Filtrar
              </b-button>
              <b-button variant="outline-secondary" size="sm" :disabled="isLoading" @click="resetPeriod">
                Últimos 12 meses
              </b-button>
            </form>
            <small class="period-help">Período inicial: últimos 12 meses. Altere as datas para consultar o histórico.</small>
          </div>
        </div>
        <p v-if="filterError" class="filter-error" role="alert">{{ filterError }}</p>
        <p v-if="loadError" class="filter-error" role="alert">{{ loadError }}</p>
        <p class="applied-period" role="status" aria-live="polite">
          <template v-if="appliedFilters">
            Pedidos e notas de <strong>{{ formatFilterDate(appliedFilters.dataInicio) }}</strong>
            a <strong>{{ formatFilterDate(appliedFilters.dataFim) }}</strong> (inclusive).
          </template>
          <template v-else>{{ isLoading ? 'Carregando o período selecionado…' : 'Nenhum período carregado.' }}</template>
          <span v-if="hasPendingFilters && !isLoading"> Datas alteradas: clique em Filtrar para aplicar.</span>
        </p>
        <small class="period-help">Títulos e saldos em aberto mostram a situação atual, independentemente do período.</small>
      </div>

      <div class="stats-grid">
        <div class="stat-card stat-primary">
          <div class="stat-icon"><feather-icon icon="ShoppingBagIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Total de Pedidos</span>
            <span class="stat-value">{{ data.resumo.totalPedidos || 0 }}</span>
          </div>
        </div>
        <div class="stat-card stat-success">
          <div class="stat-icon"><feather-icon icon="DollarSignIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Total Compras</span>
            <span class="stat-value">{{ formatCurrency(data.resumo.valorTotalCompras) }}</span>
          </div>
        </div>
        <div class="stat-card stat-warning">
          <div class="stat-icon"><feather-icon icon="FileTextIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Notas Fiscais</span>
            <span class="stat-value">{{ data.notasFiscais.totalNotas || 0 }}</span>
          </div>
        </div>
        <div class="stat-card" :class="Number(data.titulosPendentes.titulosVencidos) > 0 ? 'stat-danger' : 'stat-info'">
          <div class="stat-icon"><feather-icon icon="AlertCircleIcon" size="24" /></div>
          <div class="stat-content">
            <span class="stat-label">Títulos Vencidos</span>
            <span class="stat-value">{{ data.titulosPendentes.titulosVencidos || 0 }}</span>
          </div>
        </div>
      </div>

      <b-row class="mt-2">
        <b-col md="8">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Histórico de Compras (R$)</h5>
            <vue-apex-charts
              v-if="comprasMensaisOptions.xaxis.categories.length"
              type="area"
              height="320"
              :options="comprasMensaisOptions"
              :series="comprasMensaisSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
        <b-col md="4">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Status dos Pedidos</h5>
            <vue-apex-charts
              v-if="statusOptions.labels.length"
              type="donut"
              height="320"
              :options="statusOptions"
              :series="statusSeries"
            />
            <div v-else class="no-data">Sem dados disponíveis</div>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col md="5">
          <b-card class="info-card">
            <h5 class="card-chart-title">Resumo Financeiro</h5>
            <div class="info-list">
              <div class="info-item">
                <span class="info-label">Ticket Médio</span>
                <span class="info-value">{{ formatCurrency(data.resumo.ticketMedio) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Total Notas Fiscais</span>
                <span class="info-value">{{ formatCurrency(data.notasFiscais.valorTotalNotas) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Saldo Títulos em Aberto</span>
                <span class="info-value text-warning">{{ formatCurrency(data.titulosPendentes.valorTotalSaldo) }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Valor Vencido</span>
                <span class="info-value text-danger">{{ formatCurrency(data.titulosPendentes.valorVencido) }}</span>
              </div>
            </div>
          </b-card>
        </b-col>
        <b-col md="7">
          <b-card class="chart-card">
            <h5 class="card-chart-title">Últimos Pedidos</h5>
            <div class="table-responsive">
              <b-table
                v-if="data.pedidosRecentes.length"
                :items="data.pedidosRecentes"
                :fields="pedidoFields"
                striped
                hover
                small
                class="mb-0"
              >
                <template #cell(pedidovalortotal)="row">
                  {{ formatCurrency(row.item.pedidovalortotal) }}
                </template>
                <template #cell(pedidodata)="row">
                  {{ row.item.pedidodata ? new Date(row.item.pedidodata).toLocaleDateString('pt-BR') : '-' }}
                </template>
                <template #cell(pedidostatusworkflow)="row">
                  <b-badge :variant="getStatusVariant(row.item.pedidostatusworkflow)">
                    {{ row.item.pedidostatusworkflow || 'Sem Status' }}
                  </b-badge>
                </template>
              </b-table>
              <div v-else class="no-data">Nenhum pedido encontrado</div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import axios from '@/libs/axios';
import { BOverlay, BRow, BCol, BCard, BButton, BFormDatepicker, BTable, BBadge } from 'bootstrap-vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';
import { defaultPeriod, validatePeriod, formatFilterDate } from './customerDashboardPeriod';

export default {
  name: 'DashboardClienteHanes',
  components: { VueApexCharts, BOverlay, BRow, BCol, BCard, BButton, BFormDatepicker, BTable, BBadge },
  data() {
    return {
      isLoading: false,
      filters: defaultPeriod(),
      appliedFilters: null,
      loadError: '',
      shortDateOptions: { day: '2-digit', month: '2-digit', year: 'numeric' },
      data: {
        meusPedidos: [],
        pedidosRecentes: [],
        titulosPendentes: {},
        notasFiscais: {},
        resumo: {},
        pedidosPorStatus: [],
      },
      pedidoFields: [
        { key: 'pedidoid', label: 'Pedido' },
        { key: 'pedidodata', label: 'Data', sortable: true },
        { key: 'pedidovalortotal', label: 'Valor', sortable: true },
        { key: 'pedidostatusworkflow', label: 'Status' },
      ],
    };
  },
  computed: {
    filterError() {
      return validatePeriod(this.filters);
    },
    hasPendingFilters() {
      return !!this.appliedFilters && (
        this.filters.dataInicio !== this.appliedFilters.dataInicio
        || this.filters.dataFim !== this.appliedFilters.dataFim
      );
    },
    chartTheme() {
      return this.$store?.state?.appConfig?.layout?.skin === 'dark' ? 'dark' : 'light';
    },
    comprasMensaisSeries() {
      return [{ name: 'Compras (R$)', data: this.data.meusPedidos.map(p => Number(p.valorTotal) || 0) }];
    },
    comprasMensaisOptions() {
      return {
        chart: { type: 'area', toolbar: { show: false } },
        xaxis: { categories: this.data.meusPedidos.map(p => p.mes || '') },
        colors: ['#16a34a'],
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0 } },
        dataLabels: { enabled: false },
        yaxis: { labels: { formatter: val => this.formatCurrencyShort(val) } },
        tooltip: { theme: this.chartTheme, y: { formatter: val => this.formatCurrency(val) } },
      };
    },
    statusSeries() {
      return this.data.pedidosPorStatus.map(s => Number(s.total) || 0);
    },
    statusOptions() {
      return {
        chart: { type: 'donut' },
        labels: this.data.pedidosPorStatus.map(s => s.status || 'N/A'),
        colors: ['#16a34a', '#2563eb', '#f59e0b', '#dc2626', '#0ea5e9', '#b8c2cc'],
        legend: { position: 'bottom' },
        tooltip: { theme: this.chartTheme },
      };
    },
  },
  async created() {
    await this.loadData();
  },
  methods: {
    formatFilterDate,
    async resetPeriod() {
      this.filters = defaultPeriod();
      await this.loadData();
    },
    formatCurrency(value) {
      return 'R$ ' + Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatCurrencyShort(value) {
      const n = Number(value || 0);
      if (n >= 1000000) return 'R$ ' + (n / 1000000).toFixed(1) + 'M';
      if (n >= 1000) return 'R$ ' + (n / 1000).toFixed(1) + 'K';
      return 'R$ ' + n.toFixed(0);
    },
    getStatusVariant(status) {
      const map = { 'Aprovado': 'success', 'Pendente': 'warning', 'Cancelado': 'danger', 'Faturado': 'info' };
      return map[status] || 'secondary';
    },
    getClienteId() {
      try {
        const clienteData = JSON.parse(localStorage.getItem('clienteData'));
        return clienteData?.cliente?.clienteid || '';
      } catch { return ''; }
    },
    async loadData() {
      if (this.isLoading || this.filterError) return;
      const period = { ...this.filters };
      this.isLoading = true;
      this.loadError = '';
      try {
        const params = { clienteid: this.getClienteId(), ...period };
        if (!params.clienteid) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Cliente não identificado',
              icon: 'AlertTriangleIcon',
              variant: 'warning',
            },
          });
          this.isLoading = false;
          return;
        }
        const { data } = await axios.get('/dashboard/cliente', { params });
        this.data = data;
        this.appliedFilters = period;
      } catch (error) {
        this.loadError = this.appliedFilters
          ? 'Não foi possível aplicar o filtro. Os dados continuam no período indicado acima.'
          : 'Não foi possível carregar os dados. Tente filtrar novamente.';
        console.error('Erro ao carregar dashboard:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar dados do dashboard',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* 
  B2B Premium Palette (Client Dashboard):
  1: #01094e (Deep Navy)
  2: #0575b5 (Ocean Blue)
  3: #00b7d6 (Vibrant Cyan)
  4: #8fdeea (Pale Sky Blue)
  5: #d6f4fa (Ice Blue)
*/
.dashboard-cliente { padding: 1rem; --info-divider-color: rgba(143, 222, 234, 0.2); }

/* Header */
.page-header { margin-bottom: 2rem; }
.header-top { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 1rem; }
.page-title { font-size: 1.75rem; font-weight: 700; color: #01094e; margin-bottom: 0.25rem; letter-spacing: -0.01em; }
.page-subtitle { color: #0575b5; font-size: 1rem; font-weight: 400; opacity: 0.8; }

/* Filters Inline */
.period-controls { max-width: 100%; min-width: 0; }
.filters-inline { display: flex; align-items: flex-end; gap: 0.75rem; flex-wrap: wrap; }
.date-field { flex: 0 0 12rem; min-width: 12rem; max-width: 100%; }
.date-field > label { display: block; margin-bottom: 0.4rem; font-size: 0.95rem; font-weight: 600; }
.filter-date { width: 100%; min-height: 2.75rem; border-radius: 8px; border: 1px solid rgba(143, 222, 234, 0.6); }
.filter-date >>> .form-control { white-space: nowrap !important; min-width: 0; overflow: hidden; text-overflow: ellipsis; font-size: 1rem; }
.filters-inline > .btn { min-height: 2.75rem; white-space: nowrap; }
.period-help { display: block; margin-top: 0.5rem; color: inherit; }
.applied-period { margin: 1rem 0 0; color: inherit; }
.filter-error { margin: 0.75rem 0 0; color: #c53030; }
body.dark-layout .dashboard-cliente .filter-error { color: #ffb4b4; }
@media (max-width: 575.98px) {
  .period-controls { width: 100%; }
  .date-field { flex: 1 1 100%; min-width: 0; }
}

/* Glassmorphic Stat Cards */
.stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1.25rem; margin-bottom: 1.5rem; }
.stat-card { 
  background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(10px); border: 1px solid rgba(143, 222, 234, 0.4); 
  border-radius: 12px; padding: 1.5rem; display: flex; align-items: center; gap: 1.25rem; 
  box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04); position: relative; 
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.3s ease; 
}
.stat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08); border-color: rgba(0, 183, 214, 0.4); }

/* Base Stat Icon */
.stat-icon { 
  width: 56px; height: 56px; border-radius: 12px; display: flex; align-items: center; justify-content: center; 
  background: linear-gradient(135deg, rgba(5, 117, 181, 0.1), rgba(0, 183, 214, 0.1));
  color: #0575b5; border: 1px solid rgba(0, 183, 214, 0.15); transition: all 0.3s ease; 
}
.stat-card:hover .stat-icon { background: linear-gradient(135deg, #0575b5, #00b7d6); color: #fff; box-shadow: 0 4px 15px rgba(0, 183, 214, 0.4); border-color: transparent; }

/* Keep contextual colors for variants inside icons */
.stat-success .stat-icon { background: rgba(22,163,74,0.1); color: #16a34a; border-color: rgba(22,163,74,0.2); }
.stat-card.stat-success:hover .stat-icon { background: linear-gradient(135deg, #16a34a, #4ade80); box-shadow: 0 4px 15px rgba(22,163,74,0.4); color: #fff; }

.stat-warning .stat-icon { background: rgba(245,158,11,0.1); color: #f59e0b; border-color: rgba(245,158,11,0.2); }
.stat-card.stat-warning:hover .stat-icon { background: linear-gradient(135deg, #f59e0b, #fbbf24); box-shadow: 0 4px 15px rgba(245,158,11,0.4); color: #fff; }

.stat-danger .stat-icon { background: rgba(220,38,38,0.1); color: #dc2626; border-color: rgba(220,38,38,0.2); }
.stat-card.stat-danger:hover .stat-icon { background: linear-gradient(135deg, #dc2626, #f87171); box-shadow: 0 4px 15px rgba(220,38,38,0.4); color: #fff; }

.stat-info .stat-icon { background: rgba(14,165,233,0.1); color: #0ea5e9; border-color: rgba(14,165,233,0.2); }
.stat-card.stat-info:hover .stat-icon { background: linear-gradient(135deg, #0ea5e9, #38bdf8); box-shadow: 0 4px 15px rgba(14,165,233,0.4); color: #fff; }

/* Typography inside Stats */
.stat-label { display: block; font-size: 0.9rem; font-weight: 500; color: #0575b5; opacity: 0.8; margin-bottom: 0.25rem; }
.stat-value { display: block; font-size: 1.75rem; font-weight: 700; color: #01094e; line-height: 1.1; }

/* Chart and Info Cards */
.chart-card, .info-card { 
  background: rgba(255, 255, 255, 0.9); backdrop-filter: blur(8px); border: 1px solid rgba(143, 222, 234, 0.25); border-radius: 12px; box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04); transition: box-shadow 0.3s ease; 
}
.chart-card:hover, .info-card:hover { box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08); }
.info-card { height: 100%; display: flex; flex-direction: column; }
.card-chart-title { font-size: 1.1rem; font-weight: 700; color: #01094e; margin-bottom: 1.25rem; letter-spacing: -0.01em; }

/* Info List */
.info-list { display: flex; flex-direction: column; gap: 0.75rem; flex: 1; justify-content: center; }
.info-item { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 0; border-bottom: 1px solid var(--info-divider-color); }
.info-item:last-child { border-bottom: none; }
.info-label { color: #0575b5; font-size: 0.95rem; font-weight: 500; }
.info-value { font-weight: 700; font-size: 1.15rem; color: #01094e; }
.info-value.text-warning { color: #f59e0b !important; }
.info-value.text-danger { color: #dc2626 !important; }

.no-data { text-align: center; padding: 3rem; color: #8fdeea; font-size: 1rem; font-weight: 500; }

/* ================= Dark Layout Overrides ================= */
body.dark-layout .dashboard-cliente .page-title { color: #d6f4fa; }
body.dark-layout .dashboard-cliente .page-subtitle { color: #8fdeea; }
body.dark-layout .dashboard-cliente .stat-card,
body.dark-layout .dashboard-cliente .chart-card,
body.dark-layout .dashboard-cliente .info-card {
  background: rgba(1, 9, 78, 0.6); border-color: rgba(143, 222, 234, 0.1); box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}
body.dark-layout .dashboard-cliente .stat-card:hover,
body.dark-layout .dashboard-cliente .chart-card:hover,
body.dark-layout .dashboard-cliente .info-card:hover {
  background: rgba(1, 9, 78, 0.8); border-color: rgba(0, 183, 214, 0.3); box-shadow: 0 12px 40px rgba(0, 183, 214, 0.15);
}
body.dark-layout .dashboard-cliente .stat-label,
body.dark-layout .dashboard-cliente .info-label { color: #8fdeea; }
body.dark-layout .dashboard-cliente .stat-value,
body.dark-layout .dashboard-cliente .info-value,
body.dark-layout .dashboard-cliente .card-chart-title { color: #d6f4fa; }
body.dark-layout .dashboard-cliente { --info-divider-color: rgba(143, 222, 234, 0.15); }
body.dark-layout .dashboard-cliente .info-value.text-warning { color: #fbbf24 !important; }
body.dark-layout .dashboard-cliente .info-value.text-danger { color: #f87171 !important; }
body.dark-layout .dashboard-cliente .no-data { color: #8fdeea; }

body.dark-layout .dashboard-cliente .stat-icon { background: rgba(0, 183, 214, 0.1); color: #00b7d6; border-color: rgba(0, 183, 214, 0.2); }
body.dark-layout .dashboard-cliente .stat-card:hover .stat-icon { background: linear-gradient(135deg, #0575b5, #00b7d6); color: #fff; box-shadow: 0 0 15px rgba(0, 183, 214, 0.5); border-color: transparent; }

/* Specific dark mode context tints */
body.dark-layout .dashboard-cliente .stat-success .stat-icon { background: rgba(22,163,74,0.1); border-color: rgba(22,163,74,0.2); }
body.dark-layout .dashboard-cliente .stat-warning .stat-icon { background: rgba(245,158,11,0.1); border-color: rgba(245,158,11,0.2); }
body.dark-layout .dashboard-cliente .stat-danger .stat-icon { background: rgba(220,38,38,0.1); border-color: rgba(220,38,38,0.2); }
body.dark-layout .dashboard-cliente .stat-info .stat-icon { background: rgba(14,165,233,0.1); border-color: rgba(14,165,233,0.2); }
</style>
